import {
    Typography,
    List,
    ListItem,
    ListItemText,
    capitalize,
} from '@mui/material';
import { ScopeT } from 'business/definitions';
import InfoModal from 'components/SharedComponents/Modals/InfoModal';
import { translateBlock } from 'i18n';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';

interface ProtectedFieldModalProps {
    onClose: () => void;
    open: boolean;
    scope: ScopeT;
}

const ProtectedTableFields = ({ fields }: { fields: string[] }) => (
    <List dense>
        {fields.map((field) => (
            <ListItem key={field}>
                <ListItemText primary={capitalize(translateBlock(field))} />
            </ListItem>
        ))}
    </List>
);

const ProtectedFieldModal = ({
    onClose,
    open,
    scope,
}: ProtectedFieldModalProps) => {
    const { t } = useTranslation();
    const { protections } = useSelector((state) => state.vision);
    const currentProtection = protections.find((p) => p.scope === scope.name);

    // Group by the fields by table to display them in the modal
    const fieldsByTable = currentProtection?.blocks.reduce(
        (acc, block) => {
            const [table, field] = block.split('.');
            const translatedTable = translateBlock(table);
            if (!acc[translatedTable]) {
                acc[translatedTable] = [];
            }
            if (field) acc[translatedTable].push(`${table}.${field}`);
            return acc;
        },
        {} as Record<string, string[]>,
    );

    const sortedFieldsByTable = fieldsByTable
        ? Object.entries(fieldsByTable).sort((a, b) => a[0].localeCompare(b[0]))
        : [];

    return (
        <InfoModal
            isOpen={open}
            title={t('adminSpace.protectedFields')}
            onClose={onClose}
        >
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                <List>
                    {sortedFieldsByTable.map(([table, field]) => (
                        <ListItem
                            key={table}
                            sx={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Typography variant="body1SemiBold">
                                {table}
                            </Typography>
                            <ProtectedTableFields fields={field} />
                        </ListItem>
                    ))}
                </List>
            </Typography>
        </InfoModal>
    );
};

export default ProtectedFieldModal;
